"use client";
import React, { useState, useEffect, useCallback } from "react";
import { Palette, RotateCcw } from "lucide-react";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { ColorPicker } from "../elements/ColorPicker";
import { AnimatedSidebarPanel } from "../../layout/AnimatedSidebarPanel";
import { ThemeService, ThemeColors } from "@/services/themeService";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface SiteStylePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ThemePreset {
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    primaryGradientStart: string;
    primaryGradientEnd: string;
    secondaryGradientStart: string;
    secondaryGradientEnd: string;
  };
}

const THEME_PRESETS: ThemePreset[] = [
  {
    name: "Ocean Blue",
    colors: {
      primary: "#0066CC",
      secondary: "#4A90E2",
      accent: "#87CEEB",
      text: "#1A1A1A",
      primaryGradientStart: "#0066CC",
      primaryGradientEnd: "#004499",
      secondaryGradientStart: "#4A90E2",
      secondaryGradientEnd: "#357ABD",
    },
  },
  {
    name: "Sunset Orange",
    colors: {
      primary: "#FF6B35",
      secondary: "#FF8E53",
      accent: "#FFB07F",
      text: "#2C2C2C",
      primaryGradientStart: "#FF6B35",
      primaryGradientEnd: "#E55A2B",
      secondaryGradientStart: "#FF8E53",
      secondaryGradientEnd: "#E67E4A",
    },
  },
  {
    name: "Forest Green",
    colors: {
      primary: "#28A745",
      secondary: "#34CE57",
      accent: "#6BCF7F",
      text: "#1B1B1B",
      primaryGradientStart: "#28A745",
      primaryGradientEnd: "#1E7E34",
      secondaryGradientStart: "#34CE57",
      secondaryGradientEnd: "#2BAE46",
    },
  },
  {
    name: "Royal Purple",
    colors: {
      primary: "#7C4DFF",
      secondary: "#9C7AFF",
      accent: "#B197FC",
      text: "#2D2D2D",
      primaryGradientStart: "#7C4DFF",
      primaryGradientEnd: "#651FFF",
      secondaryGradientStart: "#9C7AFF",
      secondaryGradientEnd: "#8363E6",
    },
  },
  {
    name: "Cherry Red",
    colors: {
      primary: "#DC3545",
      secondary: "#E57373",
      accent: "#FFCDD2",
      text: "#212529",
      primaryGradientStart: "#DC3545",
      primaryGradientEnd: "#A71E2A",
      secondaryGradientStart: "#E57373",
      secondaryGradientEnd: "#D32F2F",
    },
  },
  {
    name: "Golden Yellow",
    colors: {
      primary: "#FFC107",
      secondary: "#FFCA28",
      accent: "#FFD54F",
      text: "#1C1C1C",
      primaryGradientStart: "#FFC107",
      primaryGradientEnd: "#FF8F00",
      secondaryGradientStart: "#FFCA28",
      secondaryGradientEnd: "#FFB300",
    },
  },
  {
    name: "Teal Mint",
    colors: {
      primary: "#20C997",
      secondary: "#4DD0E1",
      accent: "#80E5FF",
      text: "#1A1A1A",
      primaryGradientStart: "#20C997",
      primaryGradientEnd: "#0FA678",
      secondaryGradientStart: "#4DD0E1",
      secondaryGradientEnd: "#26C6DA",
    },
  },
  {
    name: "Midnight Dark",
    colors: {
      primary: "#495057",
      secondary: "#6C757D",
      accent: "#ADB5BD",
      text: "#F8F9FA",
      primaryGradientStart: "#495057",
      primaryGradientEnd: "#343A40",
      secondaryGradientStart: "#6C757D",
      secondaryGradientEnd: "#5A6268",
    },
  },
  {
    name: "Pink Blush",
    colors: {
      primary: "#E91E63",
      secondary: "#F06292",
      accent: "#F8BBD9",
      text: "#2C2C2C",
      primaryGradientStart: "#E91E63",
      primaryGradientEnd: "#AD1457",
      secondaryGradientStart: "#F06292",
      secondaryGradientEnd: "#EC407A",
    },
  },
  {
    name: "Sky Blue",
    colors: {
      primary: "#03A9F4",
      secondary: "#29B6F6",
      accent: "#81D4FA",
      text: "#1E1E1E",
      primaryGradientStart: "#03A9F4",
      primaryGradientEnd: "#0277BD",
      secondaryGradientStart: "#29B6F6",
      secondaryGradientEnd: "#039BE5",
    },
  },
];

export function SiteStylePanel({ isOpen, onClose }: SiteStylePanelProps) {
  const [selectedTheme, setSelectedTheme] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colors, setColors] = useState<ThemeColors>({
    primary: "#5659FF",
    secondary: "#FFFFFF",
    accent: "#F7F7F7",
    text: "#333233",
    primaryGradientStart: "#5659FF",
    primaryGradientEnd: "#4A52E8",
    secondaryGradientStart: "#FFFFFF",
    secondaryGradientEnd: "#F8F9FA",
  });

  // Get theme from context
  const { themeColors: contextThemeColors, loadTheme } = useTheme();

  const loadThemeData = useCallback(async () => {
    setLoading(true);
    try {
      // First try to use theme from context if available
      if (contextThemeColors) {
        setColors(contextThemeColors);
        setSelectedTheme(""); // Custom theme, no preset selected
        setLoading(false);
        return;
      }

      // If context doesn't have theme, fetch from API
      const themeColors = await ThemeService.getTheme();
      if (themeColors) {
        setColors(themeColors);
        setSelectedTheme(""); // Custom theme, no preset selected
      }
    } catch (error) {
      console.error("Failed to load theme:", error);
    } finally {
      setLoading(false);
    }
  }, [contextThemeColors]);

  // Load theme data when panel opens
  useEffect(() => {
    if (isOpen) {
      loadThemeData();
    }
  }, [isOpen, loadThemeData]);

  const handleThemeChange = (themeName: string) => {
    setSelectedTheme(themeName);
    const theme = THEME_PRESETS.find((t) => t.name === themeName);
    if (theme) {
      setColors(theme.colors);
    }
  };

  const handleColorChange = (colorKey: string, value: string) => {
    setColors((prev) => ({
      ...prev,
      [colorKey]: value,
    }));
    // Reset selected theme when custom colors are used
    setSelectedTheme("");
  };

  const resetToDefault = () => {
    setColors({
      primary: "#5659FF",
      secondary: "#FFFFFF",
      accent: "#F7F7F7",
      text: "#333233",
      primaryGradientStart: "#5659FF",
      primaryGradientEnd: "#4A52E8",
      secondaryGradientStart: "#FFFFFF",
      secondaryGradientEnd: "#F8F9FA",
    });
    setSelectedTheme("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ThemeService.createTheme(colors);
      
      // Refresh theme in context after saving
      await loadTheme();
      onClose();
    } catch (error) {
      console.error("Failed to save theme:", error);
      // TODO: Show error message to user
    } finally {
      setSaving(false);
    }
  };

  const resetButton = (
    <ButtonOutline
      onClick={resetToDefault}
      className="h-8 w-8 p-0 flex items-center justify-center"
      aria-label="Reset to default"
    >
      <RotateCcw className="w-4 h-4" />
    </ButtonOutline>
  );

  return (
    <AnimatedSidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Site Style"
      subtitle="Customize colors and theme for your website"
      headerActions={resetButton}
    >
      <div className="p-4 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-sm text-muted-foreground">
              Loading theme...
            </span>
          </div>
        ) : (
          <>
            {/* Theme Preset Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Palette className="w-4 h-4" />
                <span>Theme Presets</span>
              </label>
              <Select value={selectedTheme} onValueChange={handleThemeChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a preset theme..." />
                </SelectTrigger>
                <SelectContent>
                  {THEME_PRESETS.map((theme) => (
                    <SelectItem key={theme.name} value={theme.name}>
                      <div className="flex items-center space-x-3">
                        <div className="flex space-x-1">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: theme.colors.primary }}
                          />
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: theme.colors.secondary }}
                          />
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: theme.colors.accent }}
                          />
                        </div>
                        <span>{theme.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Color Customization */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Custom Colors</h4>

              {/* Primary Colors */}
              <div className="space-y-3">
                <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Base Colors
                </h5>

                <ColorPicker
                  label="Primary Color"
                  color={colors.primary}
                  onChange={(color: string) =>
                    handleColorChange("primary", color)
                  }
                />

                <ColorPicker
                  label="Secondary Color"
                  color={colors.secondary}
                  onChange={(color: string) =>
                    handleColorChange("secondary", color)
                  }
                />

                <ColorPicker
                  label="Accent Color"
                  color={colors.accent}
                  onChange={(color: string) =>
                    handleColorChange("accent", color)
                  }
                />

                <ColorPicker
                  label="Text Color"
                  color={colors.text}
                  onChange={(color: string) => handleColorChange("text", color)}
                />
              </div>

              {/* Gradient Colors */}
              <div className="space-y-3">
                <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Gradients
                </h5>

                <div className="space-y-2">
                  <h6 className="text-xs font-medium">Primary Gradient</h6>
                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker
                      label="Start"
                      color={colors.primaryGradientStart}
                      onChange={(color: string) =>
                        handleColorChange("primaryGradientStart", color)
                      }
                    />
                    <ColorPicker
                      label="End"
                      color={colors.primaryGradientEnd}
                      onChange={(color: string) =>
                        handleColorChange("primaryGradientEnd", color)
                      }
                    />
                  </div>
                  {/* Gradient Preview */}
                  <div
                    className="h-8 rounded border border-border dark:border-auth-background"
                    style={{
                      background: `linear-gradient(90deg, ${colors.primaryGradientStart}, ${colors.primaryGradientEnd})`,
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <h6 className="text-xs font-medium">Secondary Gradient</h6>
                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker
                      label="Start"
                      color={colors.secondaryGradientStart}
                      onChange={(color: string) =>
                        handleColorChange("secondaryGradientStart", color)
                      }
                    />
                    <ColorPicker
                      label="End"
                      color={colors.secondaryGradientEnd}
                      onChange={(color: string) =>
                        handleColorChange("secondaryGradientEnd", color)
                      }
                    />
                  </div>
                  {/* Gradient Preview */}
                  <div
                    className="h-8 rounded border border-border dark:border-auth-background"
                    style={{
                      background: `linear-gradient(90deg, ${colors.secondaryGradientStart}, ${colors.secondaryGradientEnd})`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Preview Section */}
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Preview
              </h5>
              <div
                className="p-4 rounded-lg border border-border dark:border-auth-background space-y-3"
                style={{ backgroundColor: colors.secondary }}
              >
                <div className="flex space-x-2">
                  <div
                    className="px-3 py-1 rounded text-white text-xs font-medium"
                    style={{ backgroundColor: colors.primary }}
                  >
                    Primary Button
                  </div>
                  <div
                    className="px-3 py-1 rounded text-xs font-medium border"
                    style={{
                      backgroundColor: colors.accent,
                      color: colors.text,
                      borderColor: colors.primary,
                    }}
                  >
                    Secondary Button
                  </div>
                </div>
                <p className="text-sm" style={{ color: colors.text }}>
                  This is how your text will appear with the selected colors.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-border dark:border-auth-background">
              <Button
                variant="default"
                size="pill-lg"
                onClick={handleSave}
                className="w-full"
                disabled={saving || loading}
                loading={saving}
              >
                {saving ? "Applying..." : "Apply Site Style"}
              </Button>
            </div>
          </>
        )}
      </div>
    </AnimatedSidebarPanel>
  );
}
